import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Combobox } from "@/components/Combobox";
import { ShoppingCart, Plus, Send, Package2, X, Trash2, CheckCircle2 } from "lucide-react";

const PRIORITIES = ["low", "medium", "high"];
const STATUS_MAP = { draft: "draft", sent: "confirmed", partially_received: "in_progress", received: "completed", closed: "completed", cancelled: "blocked" };

export default function Purchases() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, ps, sup] = await Promise.all([
        api.get("/inventory?limit=2000"),
        api.get("/purchases"),
        api.get("/suppliers"),
      ]);
      setItems(inv.data.items || []);
      setPos(ps.data.purchase_orders || []);
      setSuppliers(sup.data.suppliers || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? pos : pos.filter((p) => p.status === filter);

  const markSent = async (po) => {
    try { await api.put(`/purchases/${po.id}/status`, { status: "sent" }); toast.success("PO marked as sent"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };
  const cancelPO = async (po) => {
    if (!window.confirm("Delete this PO?")) return;
    try { await api.delete(`/purchases/${po.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <AppShell title="Purchase Orders" subtitle={`${pos.length} POs`}
      headerActions={<Button size="sm" onClick={() => setOpen(true)} data-testid="po-create-button"><Plus className="w-4 h-4 mr-1.5" />New PO</Button>}>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "ghost"} onClick={() => setFilter("all")}>All</Button>
        {["draft", "sent", "partially_received", "received"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "ghost"} onClick={() => setFilter(s)} className="capitalize" data-testid={`po-filter-${s}`}>
            {s.replace(/_/g, " ")} ({pos.filter((p) => p.status === s).length})
          </Button>
        ))}
      </div>
      {loading ? <Skeleton className="h-40" /> :
        filtered.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="No purchase orders" description="Create a PO to track supplier orders. Received quantities auto-increase inventory."
            action={<Button onClick={() => setOpen(true)} data-testid="po-empty-create"><Plus className="w-4 h-4 mr-1.5" />New PO</Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`po-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono text-muted-foreground">{p.po_number}</div>
                    <div className="font-heading font-semibold text-lg">{p.supplier}</div>
                  </div>
                  <StatusBadge status={STATUS_MAP[p.status] || "draft"} />
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-2">
                  <span className="capitalize">{p.priority}</span>·
                  <span>{p.lines?.length || 0} lines</span>·
                  <span className="font-medium">€{(p.total_cost || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="mt-3 space-y-1 max-h-28 overflow-y-auto">
                  {(p.lines || []).slice(0, 4).map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border border-border rounded px-2 py-1">
                      <span className="truncate">{l.item_name}</span>
                      <span className="font-mono tabular-nums">{l.received_qty || 0}/{l.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <div className="text-[11px] text-muted-foreground truncate">{new Date(p.created_at).toLocaleDateString()}</div>
                  <div className="flex items-center gap-1">
                    {p.status === "draft" && <Button size="sm" onClick={() => markSent(p)} data-testid={`po-send-${p.id}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>}
                    {["sent", "partially_received"].includes(p.status) && <Button size="sm" onClick={() => setReceiving(p)} data-testid={`po-receive-${p.id}`}><Package2 className="w-3.5 h-3.5 mr-1" />Receive</Button>}
                    {p.status === "draft" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancelPO(p)} data-testid={`po-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {open && <POForm open={open} onClose={() => setOpen(false)} onCreated={load} items={items} suppliers={suppliers} />}
      {receiving && <ReceiveDialog po={receiving} onClose={() => setReceiving(null)} onDone={load} />}
    </AppShell>
  );
}

function POForm({ open, onClose, onCreated, items, suppliers }) {
  const [form, setForm] = useState({
    supplier: "", priority: "medium", expected_date: "", notes: "",
    lines: [{ item_id: "", item_name: "", item_sku: "", quantity: 1, unit: "", unit_cost: 0, notes: "" }],
  });
  const [sub, setSub] = useState(false);

  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: s.name, label: s.name, sublabel: s.email || s.phone || "" })),
    [suppliers]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier || !form.supplier.trim()) { toast.error("Supplier is required"); return; }
    const validLines = form.lines.filter((l) => (l.item_name || l.item_id) && Number(l.quantity) > 0);
    if (validLines.length === 0) {
      toast.error("Add at least one line with name + quantity"); return;
    }
    setSub(true);
    try {
      await api.post("/purchases", {
        ...form,
        expected_date: form.expected_date ? new Date(form.expected_date).toISOString() : null,
        lines: validLines.map((l) => ({
          item_id: l.item_id || null,
          item_name: l.item_name || null,
          item_sku: l.item_sku || null,
          quantity: Number(l.quantity),
          unit: l.unit || null,
          unit_cost: Number(l.unit_cost || 0),
          notes: l.notes || null,
        })),
      });
      toast.success("PO created"); onCreated(); onClose();
    } catch (e) { toast.error(extractErr(e)); }
    setSub(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Purchase Order</DialogTitle>
          <DialogDescription>
            Fully free-form: type any supplier and any items (in or out of your inventory). Inventory only updates when receiving against an inventory-linked line.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-1.5">
              <Label>Supplier *</Label>
              <Combobox
                value={form.supplier}
                onChange={(v) => setForm((p) => ({ ...p, supplier: v }))}
                options={supplierOptions}
                placeholder="Pick a supplier or type a new one…"
                allowCustom={true}
                triggerTestId="po-form-supplier-combobox"
              />
              <div className="text-[11px] text-muted-foreground">Type any supplier name — it doesn't have to exist in your Suppliers list.</div>
            </div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2"><Label>Expected date</Label><Input type="date" value={form.expected_date} onChange={(e) => setForm((p) => ({ ...p, expected_date: e.target.value }))} /></div>
            <div className="space-y-1.5 md:col-span-3"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Items <span className="text-[11px] text-muted-foreground">(any name — doesn't have to exist in inventory)</span></Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", item_name: "", item_sku: "", quantity: 1, unit: "", unit_cost: 0, notes: "" }] }))} data-testid="po-form-add-line"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.lines.map((l, i) => {
                const itemOptions = items.map((it) => ({ value: it.id, label: it.name, sublabel: `${it.sku} · ${it.unit || ""}` }));
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border rounded p-2">
                    <div className="col-span-12 md:col-span-5 space-y-1">
                      <Label className="text-[11px]">Item name</Label>
                      <Combobox
                        value={l.item_id || l.item_name}
                        onChange={(v) => {
                          const c = [...form.lines];
                          // If selected an inventory item id, lookup its meta
                          const inv = items.find((x) => x.id === v);
                          if (inv) {
                            c[i] = { ...c[i], item_id: inv.id, item_name: inv.name, item_sku: inv.sku, unit: inv.unit, unit_cost: c[i].unit_cost || inv.unit_cost || 0 };
                          } else {
                            // Free text — clear item_id, store raw text as name
                            c[i] = { ...c[i], item_id: "", item_name: v, item_sku: "" };
                          }
                          setForm((f) => ({ ...f, lines: c }));
                        }}
                        options={itemOptions}
                        placeholder="Type a product name or pick from inventory…"
                        allowCustom={true}
                        triggerTestId={`po-form-line-${i}-item-combobox`}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1 space-y-1"><Label className="text-[11px]">Qty</Label><Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => { const c = [...form.lines]; c[i].quantity = e.target.value; setForm((f) => ({ ...f, lines: c })); }} data-testid={`po-form-line-${i}-qty`} /></div>
                    <div className="col-span-3 md:col-span-2 space-y-1"><Label className="text-[11px]">Unit</Label><Input value={l.unit} onChange={(e) => { const c = [...form.lines]; c[i].unit = e.target.value; setForm((f) => ({ ...f, lines: c })); }} placeholder="pcs, kg…" /></div>
                    <div className="col-span-3 md:col-span-2 space-y-1"><Label className="text-[11px]">Unit cost €</Label><Input type="number" step="0.01" min="0" value={l.unit_cost} onChange={(e) => { const c = [...form.lines]; c[i].unit_cost = e.target.value; setForm((f) => ({ ...f, lines: c })); }} /></div>
                    <div className="col-span-12 md:col-span-1 flex justify-end">{form.lines.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))}><X className="w-4 h-4" /></Button>}</div>
                    <div className="col-span-12 md:col-span-1 space-y-1 hidden md:block"><Label className="text-[11px]">Notes</Label><Input value={l.notes} onChange={(e) => { const c = [...form.lines]; c[i].notes = e.target.value; setForm((f) => ({ ...f, lines: c })); }} placeholder="Optional" /></div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={sub} data-testid="po-form-submit">{sub ? "Saving…" : "Create PO"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ po, onClose, onDone }) {
  const [lines, setLines] = useState(() => po.lines.map((l, i) => ({ line_index: i, received_qty: Math.max(0, (l.quantity || 0) - (l.received_qty || 0)), lot_number: "", expiry_date: "" })));
  const [sub, setSub] = useState(false);
  const submit = async () => {
    setSub(true);
    try {
      const payload = {
        lines: lines.filter((l) => Number(l.received_qty) > 0).map((l) => ({
          line_index: l.line_index,
          received_qty: Number(l.received_qty),
          lot_number: l.lot_number || null,
          expiry_date: l.expiry_date ? new Date(l.expiry_date).toISOString() : null,
        })),
      };
      if (payload.lines.length === 0) { toast.error("Nothing to receive"); setSub(false); return; }
      await api.post(`/purchases/${po.id}/receive`, payload);
      toast.success("Received — inventory updated");
      onDone(); onClose();
    } catch (e) { toast.error(extractErr(e)); }
    setSub(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Receive PO {po.po_number}</DialogTitle><DialogDescription>Enter received qty + optional lot/expiry. Inventory updates on confirm.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {po.lines.map((l, i) => (
            <div key={i} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div><div className="font-medium text-sm">{l.item_name}</div><div className="font-mono text-[11px] text-muted-foreground">{l.item_sku}</div></div>
                <div className="text-xs text-muted-foreground">Ordered: {l.quantity} · Already received: {l.received_qty || 0}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><Label className="text-[11px]">Receive qty</Label><Input type="number" step="any" min="0" value={lines[i].received_qty} onChange={(e) => { const c = [...lines]; c[i].received_qty = e.target.value; setLines(c); }} data-testid={`po-receive-qty-${i}`} /></div>
                <div><Label className="text-[11px]">Lot # (optional)</Label><Input value={lines[i].lot_number} onChange={(e) => { const c = [...lines]; c[i].lot_number = e.target.value; setLines(c); }} placeholder="LOT-2026-..." /></div>
                <div><Label className="text-[11px]">Expiry (optional)</Label><Input type="date" value={lines[i].expiry_date} onChange={(e) => { const c = [...lines]; c[i].expiry_date = e.target.value; setLines(c); }} /></div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={sub} data-testid="po-receive-submit"><CheckCircle2 className="w-4 h-4 mr-1.5" />{sub ? "Saving…" : "Confirm receive"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
